import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Clock, Users, Layers, Building2,
  Code2, AppWindow, Blocks, Braces,
  Building, Bot, Workflow, CheckCircle2,
  Video, Laptop,
  Quote
} from "lucide-react";

const IMPACT_STATS = [
  { icon: Clock, value: "100,000+", label: "Learning Hours Delivered", desc: "Comprehensive technical learning across software engineering and modern development practices." },
  { icon: Users, value: "500+", label: "Enterprise Batches Delivered", desc: "Instructor-led learning delivered for engineering, QA, DevOps, and application teams." },
  { icon: Layers, value: "30+", label: "Emerging Technology Domains", desc: "Coverage across software engineering, cloud, DevOps, AI, cybersecurity, and data technologies." },
  { icon: Building2, value: "250+", label: "Enterprise Clients", desc: "Organizations across industries rely on Skillsgain for technical learning initiatives." }
];

const LEARNING_OUTCOMES = [
  { icon: Code2, title: "Software Engineering Fundamentals", desc: "Learn the core principles of Software Engineering, including software development life cycle (SDLC), coding standards, software quality, testing, and collaborative development practices." },
  { icon: AppWindow, title: "Application Development", desc: "Develop modern web and enterprise applications using object-oriented programming, APIs, databases, and contemporary frameworks that support scalable business applications." },
  { icon: Blocks, title: "Software Design & Architecture", desc: "Design reliable software systems using architectural patterns, modular design, microservices, design principles, and best practices for enterprise applications." },
  { icon: Braces, title: "Advanced JavaScript Development", desc: "Master advanced JavaScript concepts, asynchronous programming, modern frameworks, reusable components, and performance optimization for interactive web applications." },
  { icon: Building, title: "Enterprise Software Development", desc: "Understand enterprise application architecture, system integration, secure coding practices, version control, and collaborative software delivery for large-scale business environments." },
  { icon: Bot, title: "Robotics Software Engineering", desc: "Explore robotics software development using C++, ROS, navigation algorithms, localization, mapping, and intelligent automation for real-world robotic systems." },
  { icon: Workflow, title: "Agile Engineering Practices", desc: "Apply Agile methodologies, Scrum, continuous collaboration, sprint planning, code reviews, and iterative development to improve software delivery and team productivity." },
  { icon: CheckCircle2, title: "Software Testing & Code Quality", desc: "Improve software reliability through unit testing, integration testing, debugging, code reviews, refactoring, and quality assurance practices that support maintainable applications." }
];

const DELIVERY_MODELS = [
  { icon: Video, title: "Live Virtual Training", desc: "Interactive online sessions with coding demonstrations, hands-on labs, and live instructor guidance." },
  { icon: Building, title: "Classroom Training", desc: "Instructor-led workshops featuring practical coding exercises, group activities, and real-world development scenarios." },
  { icon: Laptop, title: "Self-Paced Learning", desc: "Access recorded sessions, coding assignments, assessments, and learning resources at your convenience." },
  { icon: Layers, title: "Blended Learning", desc: "A combination of instructor-led classes, self-learning modules, coding labs, and enterprise projects for enhanced learning outcomes." }
];

const BY_THE_NUMBERS = [
  { value: "200+", label: "Corporate Learning Programs" },
  { value: "500+", label: "Instructor-Led Workshops" },
  { value: "25+", label: "Global Technology Partners" },
  { value: "50+", label: "Fortune & High-Growth Companies Served" }
];

const SoftwareEngineeringPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE SOFTWARE ENGINEERING LEARNING"
        title="Enterprise Software Engineering Training for Modern Development Teams"
        subtitle="Equip development teams with industry-relevant Software Development Courses covering Software Engineering, Application Development, Enterprise Software Development, Agile Practices, DevOps, cloud-native development, and Engineering Leadership Programs. Skillsgain delivers instructor-led learning with practical coding exercises, real-world projects, and enterprise use cases."
      />

      {/* Learning Impact */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">LEARNING IMPACT</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Trusted learning experiences for enterprise software teams</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {IMPACT_STATS.map((o, i) => (
            <Reveal key={o.label} variant="up" delay={i * 80}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                  <o.icon size={20} className="text-[#6BBE32]"/>
                </div>
                <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Software Engineering Expertise */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">SOFTWARE ENGINEERING EXPERTISE</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Practical software engineering skills for enterprise development</h2>
            <p className="mt-3 text-gray-600">Gain hands-on experience with modern programming practices, software architecture, agile methodologies, testing, deployment, and application lifecycle management through instructor-led learning and enterprise projects.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARNING_OUTCOMES.map((m, i) => (
            <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
              <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                  <m.icon size={20} className="text-white"/>
                </div>
                <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Learning Options */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">LEARNING OPTIONS</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Learning experiences designed for enterprise engineering teams</h2>
              <p className="mt-3 text-gray-600">Choose instructor-led virtual classes, classroom sessions, self-paced learning, or blended programs that align with technical learning objectives and organizational priorities.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DELIVERY_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 80}>
                <div className="flex flex-col gap-3 bg-white border border-gray-100 rounded-xl p-5 h-full sg-card">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers + Customer Success Story */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <Reveal variant="up" className="lg:col-span-1">
            <div className="bg-[#15348C] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">BY THE NUMBERS</div>
                <div className="space-y-5">
                  {BY_THE_NUMBERS.map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold">{s.value}</div>
                      <div className="text-xs text-gray-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-2">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 h-full flex flex-col">
              <Quote size={32} className="text-[#F57C20] mb-4"/>
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">CUSTOMER SUCCESS STORY</div>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Skillsgain delivered a customized software engineering learning program that enhanced our developers' expertise in application development, Agile methodologies, and enterprise software development. The practical assignments and instructor-led sessions improved collaboration, code quality, and project delivery across our engineering teams.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR ENGINEERING & TECHNOLOGY LEADERS</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Enhance Software Engineering Capabilities Across Your Teams</h2>
                <p className="mt-3 text-gray-300">Prepare software professionals with enterprise learning covering Software Engineering, Application Development, Enterprise Software Development, Agile methodologies, DevOps practices, and engineering leadership.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Experts</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default SoftwareEngineeringPage;
